import { Alert, Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer } from "react-leaflet";
import { LatLngBounds, latLngBounds, Map } from "leaflet";
import { CustomerAddressBaseDto, DayOfWeek, DeliveryMethodPlaceDto } from "./types";
import { LandCode, useDefaultValues } from "../../common/defaultValues";
import { createAxios } from "../../common/axios";
import { ListDto } from "../../common/listStore";
import { DeliveryMethodId } from "../../common/enum";
import { getResource } from "../../common/localization";
import { createEntityStore } from "../../common/entityStore";
import { propTextField } from "../../common/entityStoreForm";
import { SearchButton } from "../../common/listStoreUi";
import { Dictionary } from "lodash";
import { renderClose } from "../../common/dialog";
import { newId } from "../../common/utils";
import { CanceledError } from "axios";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.DeliveryPlace." + key, defaultMessage) as string;
}

interface StoreModel {
    query: string;
}

interface MapState {
    map: Map;
    dispose: () => void;
    initialized: boolean;
}

export function DeliveryPlaceDialog({ close, dialogProps, orderId, deliveryMethodId, deliveryMethodPlaceId, changeDeliveryMethodPlace, getCustomerAddress }: {
    close(): void;
    dialogProps: DialogProps;
    orderId: number | null;
    deliveryMethodId: DeliveryMethodId;
    deliveryMethodPlaceId: number | null | undefined;
    changeDeliveryMethodPlace(id: number | null, name: string | null, isCoDHandler: boolean): void;
    getCustomerAddress(): CustomerAddressBaseDto;
}) {
    const [address] = useState(() => getCustomerAddress());
    const landCode = useDefaultValues(state => state.clientData.landCode);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isSearching, setIsSearching] = useState(true);
    const [searches, setSearches] = useState<GeoSearch[]>([]);
    const map = useRef<MapState>();
    const [useStore] = useState(() => createEntityStore<StoreModel>({ data: { query: "" } }));
    const [selectedResult, setSelectedResult] = useState<GeoSearchResult>();
    useEffect(() => {
        async function init(signal: AbortSignal) {
            const street =
                address.strt1 === address.city ? "" :
                    address.strt1.startsWith(address.city + " ") ? address.strt1.substring(address.city.length + 1) :
                        address.strt1;
            const houseNr = address.strt2;
            let city = address.city.trim();
            if (landCode === "LV") {
                if (city.endsWith(" rajons")) { // LV rajons -> novads
                    city = city.substring(0, city.length - 7) + " novads";
                }
            }
            const postalCode = address.zipCode.trim();
            const fullAddress = `${street} ${houseNr}, ${city}, ${postalCode}`;
            useStore.getState().setClientDataProp("query", fullAddress);
            const initialSearchParams: GeoSearchParams[] = [
                { query: fullAddress },
                { query: `${city}, ${postalCode}` },
                { query: `${postalCode}` },
            ].filter(p => /[^, ]/g.test(p.query));
            for (const searchParams of initialSearchParams) {
                const results = await fetchGeolocations({ q: searchParams.query }, signal);
                setSearches(prev => [{ id: newId(), query: searchParams.query, results }, ...prev]);
                if (results.length > 0) {
                    if (deliveryMethodPlaceId == null) {
                        showSearchResult(results[0], map, setSelectedResult);
                    }
                    break;
                }
            }
        }

        var abortController = new AbortController();
        setTimeout(() => {
            if (abortController.signal.aborted) {
                return;
            }
            (async () => {
                try {
                    await init(abortController.signal);
                } catch {
                } finally {
                    setIsSearching(false);
                    setIsInitializing(false);
                }
            })();
        });
        return () => {
            abortController.abort();
        };
    }, [address, useStore, map, landCode, setSelectedResult, deliveryMethodPlaceId]);

    const [places, setPlaces] = useState<ListDto<DeliveryMethodPlaceDto>>(() => ({ items: [], fetching: false }));
    const [currentPlace, setSelectedPlace] = useState<DeliveryMethodPlaceDto | null>(null);
    const [isLoadingPlaces, setIsLoadingPlaces] = useState(true);
    function selectPlace(place: DeliveryMethodPlaceDto | null) {
        changeDeliveryMethodPlace(place?.id ?? null, place == null ? null : `${place.code} ${place.description}`, place == null ? false : place.additionalInfo?.isCODHandler ?? false);
        close();
    }
    async function search(addToHistory = true) {
        const query = useStore.getState().clientData.query.trim();
        if (query.length === 0) {
            return;
        }
        setIsSearching(true);
        const params: GeoSearchParams = { query };
        try {
            const results = await fetchGeolocations({ q: params.query }, new AbortController().signal);
            if (results.length > 0) {
                showSearchResult(results[0], map, setSelectedResult);
            }
            if (addToHistory) {
                setSearches(prev => [{ id: newId(), query: params.query, results }, ...prev]);
            }
        } catch (e) {
            console.error("Failed to search geolocation", e);
        } finally {
            setIsSearching(false);
        }
    }
    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.key === "Enter") {
            search();
        }
    }
    useEffect(() => {
        if (map.current != null) {
            map.current.dispose();
        }
    }, [map]);
    function setMap(m: Map | undefined) {
        if (m == null || m === map.current?.map) {
            return;
        }
        if (map.current != null) {
            map.current.dispose();
        }

        let abortRequest: (() => void) | undefined;
        let lastBounds: LatLngBounds | null = null;
        const mapState: MapState = {
            map: m,
            dispose() {
                abortRequest?.();
                m.removeEventListener("moveend", updatePlaces);
            },
            initialized: false,
        };
        const updatePlaces = async () => {
            const newBounds = m.getBounds();
            if (lastBounds != null && newBounds.equals(lastBounds)) {
                return;
            }

            lastBounds = newBounds;
            abortRequest?.();
            const abortController = new AbortController();
            abortRequest = () => abortController.abort();
            try {
                let bounds = m.getBounds();
                if (!mapState.initialized && deliveryMethodPlaceId != null) {
                    const { data: place } = await createAxios().get<DeliveryMethodPlaceDto>(
                        `customers/deliveryMethodPlaces/${deliveryMethodPlaceId}`, { signal: abortController.signal });
                    bounds = new LatLngBounds([place.latitude! - 0.01, place.longitude! - 0.01], [place.latitude! + 0.01, place.longitude! + 0.01]);
                }
                const { data } = await createAxios().get<ListDto<DeliveryMethodPlaceDto>>(
                    `customers/deliveryMethodPlaces?deliveryMethodId=${deliveryMethodId}` +
                    (deliveryMethodPlaceId != null ? `&id=${deliveryMethodPlaceId}` : "") +
                    `&bbox=${bounds.pad(0.5).toBBoxString()}`, { signal: abortController.signal });
                setPlaces(data);
                setIsLoadingPlaces(false);
                if (!mapState.initialized && deliveryMethodPlaceId != null && data.items.length > 0) {
                    const place = data.items.find(p => p.id === deliveryMethodPlaceId);
                    if (place != null) {
                        const result: GeoSearchResult = {
                            lat: place.latitude!,
                            lon: place.longitude!,
                            boundingbox: [place.latitude! - 0.01, place.latitude! + 0.01, place.longitude! - 0.01, place.longitude! + 0.01],
                            display_name: `${place.code} ${place.description}`,
                        };
                        setSearches(prev => [{ id: newId(), description: getText("CurrentlySelected", "Currently selected"), results: [result] }, ...prev]);
                        showSearchResult(result, map, setSelectedResult);
                        setSelectedPlace(place);
                    }
                }
                mapState.initialized = true;
            } catch (e) {
                if (!(e instanceof CanceledError)) {
                    throw e;
                }
            }
        }
        updatePlaces();
        m.addEventListener("moveend", updatePlaces);
        map.current = mapState;
    }

    return <Dialog {...dialogProps} fullScreen>
        <DialogTitle>{getText("Title", "Delivery place")}</DialogTitle>
        {renderClose(close)}
        {(isInitializing || isLoadingPlaces) && <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open>
            <CircularProgress color="inherit" />
        </Backdrop>}
        <DialogContent>
            <Grid container direction="column" height="100%">
                <Grid item container xs="auto" columnSpacing={2}>
                    <Grid item xs>{propTextField(useStore)("query", getText("Address", "Address"), { onKeyDown })}</Grid>
                    <SearchButton onClick={search} disabled={isInitializing || isSearching} />
                </Grid>
                <Grid item xs container spacing={2} overflow="hidden">
                    <Grid item xs={4} overflow="auto" height="100%">
                        {searches.map(s =>
                            <Paper key={s.id} sx={{ p: 2, mb: 2, mr: 1 }}>
                                {s.query ? <>{getText("SearchResultsFor", "Search results for: ")} <Link component="button" onClick={() => {
                                    useStore.getState().setClientDataProp("query", s.query!);
                                    search(false);
                                }}>
                                    {s.query}
                                </Link></> : s.description}
                                <ul>
                                    {s.results.map((result, index) =>
                                        <li key={index}>
                                            <Link component="button" onClick={() => showSearchResult(result, map, setSelectedResult)}
                                                color={selectedResult === result ? "#f00" : undefined}>{result.display_name}</Link>
                                        </li>)}
                                    {s.results.length === 0 && <li>{getText("NoResults", "No results found")}</li>}
                                </ul>
                            </Paper>)}
                    </Grid>
                    <Grid item xs>
                        {renderMap(places.items, selectPlace, deliveryMethodPlaceId, setMap, landCode, selectedResult, currentPlace)}
                    </Grid>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            {places.items.length < places.count! && <Alert severity="warning" sx={{ py: 0, px: 3 }}>
                {getText("DisplayingPartialResults", "Displaying {0} of {1} results. Zoom in to see more.")
                    .replaceAll("{0}", places.items.length.toString())
                    .replaceAll("{1}", places.count!.toString())}
            </Alert>}
            <Button onClick={() => selectPlace(null)} color="positive">{getText("DeliverToCustomersAddress", "Deliver to customer's address")}</Button>
            <Button onClick={close} color="negative" variant="outlined">{getResource("Ui.Close", "Close")}</Button>
        </DialogActions>
    </Dialog>;
}

// find coordinates using https://nominatim.openstreetmap.org/ui/search.html?q=SI and see response bounding box in dev tools
const countryBounds: { [landCode in LandCode]: LatLngBounds } = {
    "CZ": latLngBounds([48.551, 12.090], [51.055, 18.859]),
    "PL": latLngBounds([49.002, 14.069], [55.036, 24.145]),
    "SK": latLngBounds([47.731, 16.833], [49.613, 22.565]),
    "HU": latLngBounds([45.737, 16.113], [48.585, 22.897]),
    "EE": latLngBounds([57.509, 21.382], [59.938, 28.210]),
    "HR": latLngBounds([42.176, 13.210], [46.555, 19.447]),
    "LV": latLngBounds([55.674, 20.600], [58.085, 28.241]),
    "SI": latLngBounds([45.421, 13.375], [46.876, 16.596]),
};

function renderMap(
    places: DeliveryMethodPlaceDto[],
    selectPlace: (place: DeliveryMethodPlaceDto | null) => void,
    selectedId: number | null | undefined,
    setMap: (map: Map) => void,
    landCode: LandCode,
    selectedResult: GeoSearchResult | undefined,
    deliveryMethodPlace: DeliveryMethodPlaceDto | null) {
    const showSelectedResult = selectedResult != null && (
        deliveryMethodPlace == null ||
        deliveryMethodPlace.latitude !== selectedResult.lat ||
        deliveryMethodPlace.longitude !== selectedResult.lon
    );
    return <MapContainer preferCanvas ref={setMap}
        bounds={countryBounds[landCode]}
        maxBounds={countryBounds[landCode].pad(1)}
        style={{ height: "100%" }}>
        <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {renderPlaces(places, selectPlace, selectedId)}
        {showSelectedResult && <CircleMarker center={[selectedResult.lat, selectedResult.lon]} color="#f00">
            <Popup>
                {selectedResult.display_name}
            </Popup>
        </CircleMarker>}
    </MapContainer>;
}

function renderPlaces(places: DeliveryMethodPlaceDto[], selectPlace: (place: DeliveryMethodPlaceDto | null) => void, selectedId: number | null | undefined) {
    return places.sort((a, b) => selectedId === a.id ? 1 : selectedId === b.id ? -1 : 0)
        .filter(p => p.latitude != null && p.longitude != null)
        .map(item => <CircleMarker key={item.id} center={[item.latitude!, item.longitude!]}
            color={selectedId === item.id ? "#0d0" : "#00f"}>
            <Popup minWidth={600}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>{item.description}</Grid>
                    <Grid item container xs={5} alignContent="flex-start">
                        <Grid item xs={12}>{getText("Code", "Code:")} {item.code}</Grid>
                        <Grid item xs={12} mt={3}>{getText("Address", "Address:")}</Grid>
                        <Grid item xs={12}>{item.address}</Grid>
                        <Grid item xs={12}>{item.city}</Grid>
                        <Grid item xs={12}>{item.zipCode}</Grid>
                        <Grid item container mt={3}>
                            {item.additionalInfo?.holidays && <Grid item xs={12}>{getText("Holidays", "Holidays:")} {item.additionalInfo?.holidays}</Grid>}
                            {item.additionalInfo?.contact && <Grid item xs={12}>{getText("Contact", "Contact:")} {item.additionalInfo?.contact}</Grid>}
                            {item.additionalInfo?.contactPhone && <Grid item xs={12}>{getText("ContactPhone", "Contact phone:")} {item.additionalInfo?.contactPhone}</Grid>}
                            {item.additionalInfo?.isParcelLocker && <Grid item xs={12}>{getText("ParcelLocker", "Parcel locker")}</Grid>}
                            {item.additionalInfo?.isCODHandler && <Grid item xs={12}>{getText("CodHandler", "COD handler")}</Grid>}
                            {item.additionalInfo?.isPaymentByCardAvailable && <Grid item xs={12}>{getText("PaymentByCardAvailable", "Payment by card available")}</Grid>}
                            {item.additionalInfo?.parcelPickup && <Grid item xs={12}>{getText("ParcelPickup", "Parcel pickup:")} {item.additionalInfo?.parcelPickup}</Grid>}
                            {item.additionalInfo?.storageSize && <Grid item xs={12}>{getText("StorageSize", "Storage size:")} {item.additionalInfo?.storageSize}</Grid>}
                            {item.additionalInfo?.outdoors && <Grid item xs={12}>{getText("Outdoors", "Outdoors:")} {item.additionalInfo?.outdoors}</Grid>}
                        </Grid>
                    </Grid>
                    {(item.additionalInfo?.openings ?? []).length > 0 && (item.additionalInfo?.dateOpenings ?? []).length === 0 && <Grid item xs={7}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ padding: 1 }}>{getText("OpeningDayOfWeek", "Day of week")}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{getText("OpeningHours", "Opening hours")}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{getText("OpeningMiddayBreak", "Midday break")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {item.additionalInfo!.openings.map(p => <TableRow key={p.day}>
                                    <TableCell sx={{ padding: 1 }}>{getResource("Ui." + DayOfWeek[p.day], DayOfWeek[p.day])}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{p.openHours}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{p.midBreak}</TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>
                    </Grid>}
                    {(item.additionalInfo?.dateOpenings ?? []).length > 0 && <Grid item xs={7}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ padding: 1 }}>{getText("OpeningDate", "Date")}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{getText("OpeningDayOfWeek", "Day of week")}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{getText("OpeningHours", "Opening hours")}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{getText("OpeningType", "Type")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {item.additionalInfo!.dateOpenings.map(p => <TableRow key={p.date}>
                                    <TableCell sx={{ padding: 1 }}>{p.date}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{getResource("Ui." + DayOfWeek[p.day], DayOfWeek[p.day])}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{p.open}</TableCell>
                                    <TableCell sx={{ padding: 1 }}>{p.type}</TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>
                    </Grid>}
                    <Grid item xs={12}><Button onClick={() => selectPlace(item)}>{selectedId === item.id ? getText("CurrentlySelected", "Currently selected") : getText("Select", "Select")}</Button></Grid>
                </Grid>
            </Popup>
        </CircleMarker>);
}

function showSearchResult(result: GeoSearchResult, map: MutableRefObject<MapState | undefined>, setSelectedResult: React.Dispatch<React.SetStateAction<GeoSearchResult | undefined>>): void {
    const bounds = new LatLngBounds(
        [result.boundingbox[0], result.boundingbox[2]],
        [result.boundingbox[1], result.boundingbox[3]]);
    const width = Math.abs(result.boundingbox[1] - result.boundingbox[0]);
    const height = Math.abs(result.boundingbox[3] - result.boundingbox[2]);
    const size = Math.sqrt((width * width + height * height));
    const extend = Math.pow(size, -0.1) - 1;
    map.current?.map.fitBounds(bounds.pad(extend));
    map.current?.map.closePopup();
    setSelectedResult(result);
}

const GEO_CACHE_KEY = "geoCache";
const GEO_CACHE_LIMIT = 5;

function saveGeolocationToCache(params: Dictionary<string>, result: GeoSearchResult[]) {
    const key = JSON.stringify(params);

    // Load existing cache
    const cacheRaw = localStorage.getItem(GEO_CACHE_KEY);
    let cache: Record<string, GeoSearchResult[]> = {};
    if (cacheRaw) {
        try {
            cache = JSON.parse(cacheRaw);
        } catch (e) {
            console.error("Failed to parse geo cache", e);
        }
    }

    // Update cache with new value
    cache[key] = result;

    // Enforce limit of 5 entries
    const keys = Object.keys(cache);
    if (keys.length > GEO_CACHE_LIMIT) {
        // Remove the oldest (based on insertion order)
        const oldestKey = keys[0];
        delete cache[oldestKey];
    }

    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cache));
}

function getGeolocationFromCache(params: Dictionary<string>): GeoSearchResult[] | null {
    const key = JSON.stringify(params);

    const cacheRaw = localStorage.getItem(GEO_CACHE_KEY);
    if (!cacheRaw) return null;

    try {
        const cache = JSON.parse(cacheRaw) as Record<string, GeoSearchResult[]>;
        return cache[key] || null;
    } catch (e) {
        console.error("Failed to parse geo cache", e);
        return null;
    }
}

async function fetchGeolocations(params: Dictionary<string>, signal: AbortSignal) {
    const cached = getGeolocationFromCache(params);
    if (cached) {
        return cached;
    }
    const response = await createAxios().get<GeoSearchResult[]>(
        "https://nominatim.openstreetmap.org/search?" + new URLSearchParams({
            ...params,
            countrycodes: useDefaultValues.getState().clientData.landCode,
            format: "json",
        }), { signal, headers: { "Authorization": null } });
    saveGeolocationToCache(params, response.data);
    return response.data;
}

interface GeoSearch {
    id: number;
    query?: string;
    description?: string;
    results: GeoSearchResult[];
}

interface GeoSearchResult {
    lat: number;
    lon: number;
    boundingbox: [number, number, number, number];
    display_name: string;
}

interface GeoSearchParams {
    query: string;
}